/**
 * /golden-path — public marketing route.
 *
 * The visual operating narrative of ChainFlow OS — eight cinematic
 * sections walking estate agents through a live property transaction.
 * Preserves the approved ChainFlow visual identity (violet→indigo
 * Golden Path palette, light-mode dominant, subtle dark accents,
 * cinematic glassmorphism, premium spacing rhythm).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, CheckCircle2, Sparkles, ShieldCheck, ShieldAlert, Clock,
  TrendingUp, TrendingDown, MessageSquare, FileText, Home, Search, PoundSterling,
  AlertTriangle, Users, Bell, Calendar, Eye, Zap, Target, Briefcase,
  Activity, Layers, GitBranch, MapPin, Phone, Mail, Apple, Smartphone, Tablet, Monitor,
} from "lucide-react";
import { BrandIcon, BrandWord } from "../components/brand/BrandLogo";

/* ═══════════════════════════════════════════════════════════════════
   Shared design tokens (mirror Golden Path master)
   ─────────────────────────────────────────────────────────────────── */
const T = {
  textPrimary: "#0F172A",
  textMuted:   "#475569",
  textSubtle:  "#64748B",
  textFaint:   "#94A3B8",
  violet:      "#7C3AED",
  violetDeep:  "#6D28D9",
  indigo:      "#4F46E5",
  surface:     "linear-gradient(180deg, #FAFAFD 0%, #F4F2FB 100%)",
  card:        "#FFFFFF",
  cardShadow:  "0 24px 50px -28px rgba(15,23,42,0.18), 0 6px 14px -8px rgba(15,23,42,0.06)",
  border:      "1px solid rgba(15,23,42,0.06)",
  gradientCTA: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)",
  ctaShadow:   "0 14px 28px -10px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
};

const Eyebrow = ({ children }) => (
  <p className="text-[10.5px] font-bold uppercase tracking-[0.22em]" style={{ color: T.violetDeep }}>
    <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle cf-pulse" style={{ background: T.violet }} />
    {children}
  </p>
);

const GradientWord = ({ children }) => (
  <span style={{
    background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 50%, #4F46E5 100%)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
  }}>{children}</span>
);

const PrimaryButton = ({ to = "/signup", children, testid }) => (
  <Link
    to={to}
    className="h-12 px-6 rounded-xl text-[13.5px] font-semibold inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
    style={{ background: T.gradientCTA, color: "#FFFFFF", boxShadow: T.ctaShadow }}
    data-testid={testid}
  >
    {children} <ArrowRight size={14} />
  </Link>
);

const SecondaryButton = ({ to = "#golden-path-flow", children, testid }) => (
  <a
    href={to}
    className="h-12 px-6 rounded-xl text-[13.5px] font-semibold inline-flex items-center gap-2 transition-all hover:bg-violet-50"
    style={{ background: "#FFFFFF", color: T.textPrimary, border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 6px 14px -8px rgba(15,23,42,0.08)" }}
    data-testid={testid}
  >
    {children}
  </a>
);

/* ═══════════════════════════════════════════════════════════════════
   Reused glass operational card (used in hero composition)
   ─────────────────────────────────────────────────────────────────── */
const GlassCard = ({ Icon, kicker, title, tone = "violet", pillText, pillTone, className = "" }) => {
  const tones = {
    emerald: { bg: "#ECFDF5", fg: "#047857", ring: "rgba(16,185,129,0.22)" },
    amber:   { bg: "#FFFBEB", fg: "#B45309", ring: "rgba(245,158,11,0.22)" },
    violet:  { bg: "#F5F3FF", fg: "#6D28D9", ring: "rgba(124,58,237,0.22)" },
    rose:    { bg: "#FEF2F2", fg: "#B91C1C", ring: "rgba(239,68,68,0.22)" },
  }[tone];
  const pillStyles = {
    rose:   { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" },
    violet: { bg: "#F5F3FF", fg: "#6D28D9", border: "#E9D5FF" },
    emerald:{ bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0" },
  };
  const pill = pillTone ? pillStyles[pillTone] : null;
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-2xl bg-white/95 backdrop-blur-xl shadow-[0_24px_50px_-20px_rgba(15,23,42,0.22),0_8px_18px_-10px_rgba(124,58,237,0.15)] ${className}`}
      style={{ border: T.border, maxWidth: 250 }}
    >
      <span className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
            style={{ background: tones.bg, color: tones.fg, border: `1px solid ${tones.ring}` }}>
        <Icon size={15} strokeWidth={2.1} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11.5px] font-bold leading-tight" style={{ color: T.textPrimary }}>{kicker}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <p className="text-[10.5px] truncate leading-tight" style={{ color: T.textSubtle }}>{title}</p>
          {pill && (
            <span className="text-[8.5px] font-bold uppercase tracking-[0.10em] px-1.5 h-[15px] inline-flex items-center rounded-full shrink-0"
                  style={{ background: pill.bg, color: pill.fg, border: `1px solid ${pill.border}` }}>{pillText}</span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Top nav — minimal, calm, public
   ─────────────────────────────────────────────────────────────────── */
const TopNav = () => (
  <nav className="sticky top-0 z-40 backdrop-blur-xl" style={{ background: "rgba(250,250,253,0.85)", borderBottom: "1px solid rgba(15,23,42,0.05)" }}>
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <BrandIcon size={26} />
        <BrandWord tone="dark" size={17} />
        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-violet-700 ml-1 px-1.5 h-[16px] rounded-md inline-flex items-center" style={{ background: "#F5F3FF", border: "1px solid #DDD6FE" }}>OS</span>
      </Link>
      <div className="hidden md:flex items-center gap-7 text-[12.5px] font-semibold" style={{ color: T.textMuted }}>
        <Link to="/" className="hover:text-slate-900">Home</Link>
        <Link to="/golden-path" className="text-slate-900">The Golden Path</Link>
        <Link to="/pricing" className="hover:text-slate-900">Pricing</Link>
        <Link to="/login" className="hover:text-slate-900">Sign in</Link>
      </div>
      <Link to="/signup"
            className="h-9 px-3.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5"
            style={{ background: T.gradientCTA, color: "#FFFFFF", boxShadow: T.ctaShadow }}
            data-testid="nav-book-demo">
        Book Demo <ArrowRight size={12} />
      </Link>
    </div>
  </nav>
);

/* ═══════════════════════════════════════════════════════════════════
   SECTION 1 — HERO
   ─────────────────────────────────────────────────────────────────── */
const HERO_PILLS = [
  "Built for UK Estate Agencies",
  "Multi-Party Collaboration",
  "Reduce Fall-Throughs",
  "Operational Visibility",
  "Transaction Intelligence",
];

const HeroDealComposition = () => (
  <div className="relative h-[520px] lg:h-[580px]">
    {/* Ambient violet halo */}
    <div aria-hidden="true" className="absolute inset-0 -z-10">
      <div className="absolute right-[6%] top-[10%] w-[420px] h-[420px] rounded-full"
           style={{ background: "radial-gradient(circle, rgba(124,58,237,0.20) 0%, rgba(124,58,237,0) 65%)", filter: "blur(20px)" }} />
    </div>

    {/* Main Deal Room card */}
    <div
      className="absolute right-0 top-4 w-[540px] max-w-[92%] rounded-2xl overflow-hidden"
      style={{ background: "#FFFFFF", border: T.border, boxShadow: T.cardShadow }}
    >
      {/* Header */}
      <div className="px-5 py-4 flex items-center gap-3" style={{ borderBottom: "1px solid rgba(15,23,42,0.05)" }}>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] inline-flex items-center gap-1.5" style={{ color: T.violetDeep }}>
            <span className="w-1.5 h-1.5 rounded-full cf-pulse" style={{ background: T.violet }} />
            Live Deal Room
          </p>
          <p className="font-display text-[14px] font-bold leading-tight mt-1" style={{ color: T.textPrimary }}>23 King Street, Manchester</p>
          <p className="text-[10.5px]" style={{ color: T.textSubtle }}>Ref · START-7264 · Sale · Freehold</p>
        </div>
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] px-2 h-[20px] inline-flex items-center rounded-full"
              style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}>On Track</span>
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-3 p-4">
        {/* Timeline */}
        <div className="rounded-xl p-3" style={{ background: "#FAFBFD", border: T.border }}>
          <p className="text-[9px] font-bold uppercase tracking-[0.14em] mb-2" style={{ color: T.textFaint }}>Progression timeline</p>
          <ul className="space-y-1.5">
            {[
              { dot: "#10B981", t: "Instruction received",   d: "12 Apr" },
              { dot: "#10B981", t: "GA agreed",              d: "14 Apr" },
              { dot: "#10B981", t: "Mortgage offer issued",  d: "22 Apr" },
              { dot: "#F59E0B", t: "Enquiries open",         d: "4d ago", risk: true },
              { dot: "#FFF",    t: "Exchange target",        d: "24 May", active: true },
            ].map((r, i) => (
              <li key={i} className="grid grid-cols-[8px_minmax(0,1fr)_auto] items-center gap-2 px-2 py-1.5 rounded-md"
                  style={r.active ? { background: T.gradientCTA } : r.risk ? { background: "#FFFBEB" } : { background: "#FFFFFF" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.dot, boxShadow: r.active ? "0 0 0 3px rgba(255,255,255,0.30)" : "none" }} />
                <span className="text-[10.5px] font-bold truncate" style={{ color: r.active ? "#FFFFFF" : T.textPrimary }}>{r.t}</span>
                <span className="text-[9px] tabular-nums" style={{ color: r.active ? "rgba(255,255,255,0.75)" : T.textFaint }}>{r.d}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Right intelligence rail */}
        <div className="space-y-2">
          <div className="rounded-xl p-3" style={{ background: "#FAFBFD", border: T.border }}>
            <p className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: T.textFaint }}>Deal health</p>
            <p className="font-display text-[20px] font-bold tabular-nums leading-none mt-1.5" style={{ color: T.violetDeep }}>82<span className="text-[12px] font-semibold" style={{ color: T.textFaint }}>/100</span></p>
            <p className="text-[9.5px] mt-1" style={{ color: "#047857" }}>+9 vs last week</p>
          </div>
          <div className="rounded-xl p-3" style={{ background: "#FAFBFD", border: T.border }}>
            <p className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: T.textFaint }}>Exchange in</p>
            <p className="font-display text-[20px] font-bold tabular-nums leading-none mt-1.5" style={{ color: T.textPrimary }}>9 days</p>
            <p className="text-[9.5px] mt-1" style={{ color: T.textSubtle }}>78% probability</p>
          </div>
          {/* Participant strip */}
          <div className="rounded-xl p-3 flex items-center gap-1.5" style={{ background: "#FAFBFD", border: T.border }}>
            {[
              { i: "EP", g: "linear-gradient(135deg,#6D28D9,#4F46E5)" },
              { i: "SL", g: "linear-gradient(135deg,#0EA5E9,#0369A1)" },
              { i: "MT", g: "linear-gradient(135deg,#10B981,#047857)" },
              { i: "RW", g: "linear-gradient(135deg,#F59E0B,#B45309)" },
            ].map((a, i) => (
              <span key={i} className="w-5 h-5 rounded-full text-white text-[8px] font-bold inline-flex items-center justify-center ring-2 ring-white" style={{ background: a.g, marginLeft: i === 0 ? 0 : -6 }}>{a.i}</span>
            ))}
            <span className="text-[9px] font-semibold ml-1" style={{ color: T.textSubtle }}>+3 active</span>
          </div>
        </div>
      </div>
    </div>

    {/* Floating cards */}
    <GlassCard Icon={PoundSterling} kicker="Mortgage offer issued" title="NatWest · 2 mins ago" tone="violet"
               className="absolute top-[8%] left-[2%] z-20 hidden md:flex" />
    <GlassCard Icon={ShieldAlert} kicker="Solicitor awaiting reply" title="4 days inactive" tone="amber"
               pillTone="rose" pillText="High risk" className="absolute top-[44%] left-[-4%] z-20 hidden md:flex" />
    <GlassCard Icon={CheckCircle2} kicker="Searches returned" title="Local Authority · just now" tone="emerald"
               className="absolute bottom-[6%] left-[6%] z-20 hidden md:flex" />
  </div>
);

const HeroSection = () => (
  <section className="relative max-w-[1320px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-16 lg:pb-24" data-testid="gp-hero">
    {/* Background grain */}
    <div aria-hidden="true" className="absolute inset-0 -z-10 pointer-events-none"
         style={{ background: "radial-gradient(ellipse 1200px 600px at 60% -10%, rgba(124,58,237,0.08) 0%, rgba(124,58,237,0) 65%)" }} />

    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-12 lg:gap-16 items-center">
      {/* Left */}
      <div>
        {/* Premium pills */}
        <div className="flex flex-wrap gap-1.5 mb-6">
          {HERO_PILLS.map((p) => (
            <span key={p} className="text-[10px] font-semibold uppercase tracking-[0.10em] px-2.5 h-[22px] inline-flex items-center rounded-full"
                  style={{ background: "#FFFFFF", color: T.textMuted, border: T.border, boxShadow: "0 2px 6px -2px rgba(15,23,42,0.05)" }}>
              <span className="w-1 h-1 rounded-full mr-1.5" style={{ background: T.violet }} />
              {p}
            </span>
          ))}
        </div>

        <h1 className="font-display text-[40px] sm:text-[48px] lg:text-[58px] font-bold tracking-[-0.028em] leading-[1.02]" style={{ color: T.textPrimary }} data-testid="gp-hero-headline">
          The operating system for{" "}
          <GradientWord>property transactions.</GradientWord>
        </h1>
        <p className="text-[15px] lg:text-[16px] leading-relaxed mt-5 max-w-[560px]" style={{ color: T.textMuted }}>
          From instruction to completion — ChainFlow keeps every deal moving with structured
          communication, operational visibility, and real-time progression.
        </p>

        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <PrimaryButton to="/signup" testid="gp-hero-cta-primary">Book Demo</PrimaryButton>
          <SecondaryButton to="#golden-path-flow" testid="gp-hero-cta-secondary">See The Flow</SecondaryButton>
        </div>

        <p className="text-[11.5px] mt-6 inline-flex items-center gap-2" style={{ color: T.textSubtle }}>
          <span className="w-1.5 h-1.5 rounded-full cf-pulse" style={{ background: "#10B981" }} />
          Live with UK estate agencies · 30-second onboarding · No card required
        </p>
      </div>

      {/* Right composition */}
      <HeroDealComposition />
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════
   SECTION 2 — THE PROBLEM
   ─────────────────────────────────────────────────────────────────── */
const ProblemSection = () => {
  const chaos = [
    { Icon: MessageSquare, t: "WhatsApp threads",   sub: "Critical updates lost in 11 different chats." },
    { Icon: FileText,      t: "Spreadsheet tracking", sub: "Manually updated, instantly out of date." },
    { Icon: Mail,          t: "Endless email chains", sub: "Re:Re:Fwd:Re — no one knows the latest." },
    { Icon: AlertTriangle, t: "Missed updates",     sub: "Solicitor went quiet — nobody noticed for 9 days." },
    { Icon: Briefcase,     t: "Document confusion", sub: "Three versions of the TA6 in three places." },
    { Icon: Clock,         t: "Progression bottlenecks", sub: "Stalled on the same broker phone-tag for weeks." },
  ];
  return (
    <section className="relative py-20 lg:py-28 overflow-hidden" data-testid="gp-problem"
             style={{ background: "linear-gradient(180deg, #FAFAFD 0%, #F4F2FB 50%, #1B1D3A 100%)" }}>
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <Eyebrow>The reality today</Eyebrow>
          <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[48px] font-bold tracking-[-0.026em] leading-[1.05] mt-3" style={{ color: T.textPrimary }}>
            What agencies use today is <GradientWord>operational chaos.</GradientWord>
          </h2>
          <p className="text-[15px] leading-relaxed mt-5 max-w-[640px]" style={{ color: T.textMuted }}>
            Every property transaction touches 6+ parties, 30+ documents and 12+ weeks of progression.
            And yet the average agency runs all of it through tools that were never designed for transactions.
          </p>
        </div>

        {/* Chaos grid */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {chaos.map(({ Icon, t, sub }) => (
            <div key={t} className="rounded-2xl p-5" style={{ background: "#FFFFFF", border: T.border, boxShadow: "0 12px 24px -16px rgba(15,23,42,0.10)" }}>
              <span className="w-10 h-10 rounded-xl inline-flex items-center justify-center mb-3"
                    style={{ background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA" }}>
                <Icon size={16} />
              </span>
              <p className="font-display text-[14px] font-bold" style={{ color: T.textPrimary }}>{t}</p>
              <p className="text-[12.5px] mt-1.5 leading-snug" style={{ color: T.textSubtle }}>{sub}</p>
            </div>
          ))}
        </div>

        {/* Transformation moment */}
        <div className="mt-16 lg:mt-20 rounded-3xl p-8 lg:p-12 relative overflow-hidden"
             style={{
               background: "linear-gradient(160deg, #1B1D3A 0%, #0E0F22 100%)",
               boxShadow: "0 40px 90px -30px rgba(60,40,180,0.45)",
             }}>
          {/* Glow */}
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
               style={{ background: "radial-gradient(ellipse 800px 400px at 80% 50%, rgba(124,58,237,0.28) 0%, rgba(124,58,237,0) 60%)" }} />
          <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-6 items-center">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{ color: "#A78BFA" }}>
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle cf-pulse" style={{ background: T.violet }} />
                The shift
              </p>
              <h3 className="font-display text-[28px] sm:text-[36px] font-bold tracking-[-0.02em] leading-[1.05] text-white">
                ChainFlow <span style={{ color: "#A78BFA" }}>centralises</span> the transaction.
              </h3>
              <p className="text-[14px] leading-relaxed mt-4 max-w-[540px]" style={{ color: "rgba(255,255,255,0.75)" }}>
                One Deal Room per transaction. Every party, every document, every milestone,
                every risk — in one structured operational thread. No more chasing.
              </p>
            </div>
            <PrimaryButton to="#golden-path-flow" testid="gp-problem-cta">See it in action</PrimaryButton>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   SECTION 3 — THE GOLDEN PATH (12-step vertical timeline)
   ─────────────────────────────────────────────────────────────────── */
const GOLDEN_STEPS = [
  { n: 1,  title: "Instruction Received",       sub: "New seller signs the agreement", participants: ["Seller", "Agent"],          status: "done",   Icon: Home,         insight: "Property auto-imported with full address, tenure and EPC." },
  { n: 2,  title: "Seller Launch Hub Activated", sub: "Vendor onboarding portal opens",  participants: ["Seller"],                   status: "done",   Icon: Zap,          insight: "Self-serve TA6/TA10 completion in under 12 minutes." },
  { n: 3,  title: "Viewings Coordinated",       sub: "Calendar, alerts, feedback loops", participants: ["Buyer", "Agent"],           status: "done",   Icon: Calendar,     insight: "Avg. 4.2 viewings per offer · feedback captured in-line." },
  { n: 4,  title: "Offer Submitted",            sub: "Buyer submits structured offer",   participants: ["Buyer", "Agent"],           status: "done",   Icon: PoundSterling, insight: "Position, chain, mortgage AIP captured at submission." },
  { n: 5,  title: "Offer Score Calculated",     sub: "AI scores offer strength",         participants: ["Agent"],                    status: "done",   Icon: Target,       insight: "Composite score: chain, finance, motivation — 87/100." },
  { n: 6,  title: "Deal Room Opens",            sub: "Transaction operating system goes live", participants: ["All parties"],        status: "active", Icon: Activity,     insight: "Single thread · structured updates · real-time progression." },
  { n: 7,  title: "Solicitors Introduced",      sub: "Both sides onboarded automatically", participants: ["Solicitors"],            status: "active", Icon: Briefcase,    insight: "Conveyancers receive secure invite + instructions pack." },
  { n: 8,  title: "Documents Uploaded",         sub: "AML, TA forms, contracts, searches", participants: ["Solicitors", "Buyer", "Seller"], status: "active", Icon: FileText, insight: "Categorised, version-controlled, request-tracked." },
  { n: 9,  title: "Risks Detected",             sub: "Stalled replies, expiring offers, document gaps", participants: ["Agent"],    status: "active", Icon: ShieldAlert,  insight: "AI flags 7 risk patterns — agents are warned days before." },
  { n: 10, title: "Mortgage + Chain Progress",  sub: "Live status across the whole chain", participants: ["Broker", "Chain agents"], status: "live",  Icon: GitBranch,    insight: "Every chain link visible · expiry countdowns tracked." },
  { n: 11, title: "Exchange Ready",             sub: "All preconditions met",            participants: ["Solicitors", "Buyer", "Seller"], status: "future", Icon: CheckCircle2, insight: "Pre-exchange checklist auto-validated · 78% probability." },
  { n: 12, title: "Completion Achieved",        sub: "Keys handed over · deal closed",   participants: ["All parties"],              status: "future", Icon: Sparkles,     insight: "Audit trail archived · referral loop activated." },
];

const StepCard = ({ step }) => {
  const statusMap = {
    done:   { pill: "Completed", bg: "#ECFDF5", color: "#047857", border: "#A7F3D0", numBg: "linear-gradient(135deg, #10B981, #047857)" },
    active: { pill: "In progress", bg: "#F5F3FF", color: "#6D28D9", border: "#E9D5FF", numBg: "linear-gradient(135deg, #7C3AED, #6D28D9)" },
    live:   { pill: "Live", bg: "#F5F3FF", color: "#6D28D9", border: "#E9D5FF", numBg: "linear-gradient(135deg, #7C3AED, #4F46E5)" },
    future: { pill: "Upcoming", bg: "#F1F5F9", color: "#475569", border: "#E2E8F0", numBg: "linear-gradient(135deg, #94A3B8, #64748B)" },
  };
  const s = statusMap[step.status];

  return (
    <article
      className="relative grid grid-cols-[48px_minmax(0,1fr)] gap-4 lg:gap-6"
      data-testid={`gp-step-${step.n}`}
    >
      {/* Number badge */}
      <div className="relative flex flex-col items-center">
        <span
          className="w-12 h-12 rounded-full text-white font-bold text-[15px] inline-flex items-center justify-center z-10"
          style={{ background: s.numBg, boxShadow: "0 10px 20px -8px rgba(99,102,241,0.45), inset 0 1px 0 rgba(255,255,255,0.22)" }}
        >
          {String(step.n).padStart(2, "0")}
        </span>
        {/* Connecting line */}
        <span aria-hidden="true" className="absolute top-12 bottom-[-48px] w-px"
              style={{ background: "linear-gradient(180deg, rgba(124,58,237,0.35) 0%, rgba(124,58,237,0.05) 100%)" }} />
      </div>

      {/* Card */}
      <div className="rounded-2xl p-5 lg:p-6 -mt-1 mb-12 transition-all hover:-translate-y-0.5"
           style={{ background: "#FFFFFF", border: T.border, boxShadow: T.cardShadow }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="w-8 h-8 rounded-lg inline-flex items-center justify-center"
                    style={{ background: "#F5F3FF", color: T.violetDeep, border: "1px solid #E9D5FF" }}>
                <step.Icon size={14} />
              </span>
              <h3 className="font-display text-[18px] lg:text-[20px] font-bold tracking-[-0.012em]" style={{ color: T.textPrimary }}>
                {step.title}
              </h3>
              <span className="text-[9.5px] font-bold uppercase tracking-[0.10em] px-2 h-[20px] inline-flex items-center rounded-full"
                    style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
                {s.pill}
              </span>
            </div>
            <p className="text-[13px] mt-1.5" style={{ color: T.textSubtle }}>{step.sub}</p>
          </div>
        </div>

        {/* Insight + participants */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-4">
          <div className="rounded-xl p-3.5"
               style={{ background: "linear-gradient(160deg, #F5F3FF 0%, #FFFFFF 100%)", border: "1px solid rgba(124,58,237,0.16)" }}>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] mb-1.5 inline-flex items-center gap-1.5" style={{ color: T.violetDeep }}>
              <Sparkles size={10} /> Operational insight
            </p>
            <p className="text-[12.5px] leading-snug" style={{ color: T.textPrimary }}>{step.insight}</p>
          </div>
          <div className="rounded-xl p-3.5" style={{ background: "#FAFBFD", border: T.border }}>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] mb-1.5" style={{ color: T.textFaint }}>Parties involved</p>
            <div className="flex flex-wrap gap-1.5">
              {step.participants.map((p) => (
                <span key={p} className="text-[10.5px] font-semibold px-2 h-[20px] inline-flex items-center rounded-full"
                      style={{ background: "#FFFFFF", color: T.textMuted, border: T.border }}>{p}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

const GoldenPathSection = () => (
  <section id="golden-path-flow" className="relative py-20 lg:py-28" data-testid="gp-flow"
           style={{ background: T.surface }}>
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
      <div className="max-w-3xl mb-14">
        <Eyebrow>The Golden Path</Eyebrow>
        <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[48px] font-bold tracking-[-0.026em] leading-[1.05] mt-3" style={{ color: T.textPrimary }}>
          12 steps. <GradientWord>One operational thread.</GradientWord>
        </h2>
        <p className="text-[15px] leading-relaxed mt-5" style={{ color: T.textMuted }}>
          Every property transaction follows the same arc. ChainFlow gives that arc a shape —
          a single structured path that every party can see, contribute to and trust.
        </p>
      </div>

      <div className="max-w-[920px] mx-auto">
        {GOLDEN_STEPS.map((step) => <StepCard key={step.n} step={step} />)}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════
   SECTION 4 — LIVE OPERATIONAL INTELLIGENCE
   ─────────────────────────────────────────────────────────────────── */
const Spark = ({ points, stroke }) => {
  const w = 110, h = 32, pad = 2;
  const max = Math.max(...points), min = Math.min(...points);
  const range = Math.max(1, max - min);
  const step = (w - pad * 2) / Math.max(1, points.length - 1);
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"}${(pad + i * step).toFixed(1)},${(h - pad - ((p - min) / range) * (h - pad * 2)).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-8" aria-hidden="true">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const IntelWidget = ({ label, value, unit, pill, pillTone, sub, subTone, sparkPoints, sparkStroke, Icon }) => {
  const pillStyles = {
    emerald: { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0" },
    violet:  { bg: "#F5F3FF", fg: "#6D28D9", border: "#E9D5FF" },
    rose:    { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" },
    amber:   { bg: "#FFFBEB", fg: "#B45309", border: "#FDE68A" },
  };
  const ps = pillStyles[pillTone];
  return (
    <div className="rounded-2xl p-5 transition-transform hover:-translate-y-0.5" style={{ background: "#FFFFFF", border: T.border, boxShadow: T.cardShadow }}>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg inline-flex items-center justify-center" style={{ background: "#F5F3FF", color: T.violetDeep, border: "1px solid #E9D5FF" }}>
          <Icon size={14} />
        </span>
        <p className="text-[11.5px] font-bold" style={{ color: T.textMuted }}>{label}</p>
      </div>
      <div className="flex items-baseline gap-2">
        <p className="font-display text-[28px] font-bold tabular-nums leading-none" style={{ color: T.textPrimary }}>{value}<span className="text-[14px] font-semibold ml-0.5" style={{ color: T.textFaint }}>{unit}</span></p>
        {pill && <span className="text-[10.5px] font-bold uppercase tracking-[0.10em] px-2 h-[20px] inline-flex items-center rounded-full" style={{ background: ps.bg, color: ps.fg, border: `1px solid ${ps.border}` }}>{pill}</span>}
      </div>
      <p className="text-[11.5px] mt-2.5" style={{ color: subTone === "rose" ? "#B91C1C" : subTone === "emerald" ? "#047857" : T.textSubtle }}>{sub}</p>
      {sparkPoints && <div className="mt-2"><Spark points={sparkPoints} stroke={sparkStroke} /></div>}
    </div>
  );
};

const IntelligenceSection = () => (
  <section className="py-20 lg:py-28" data-testid="gp-intelligence">
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
      <div className="max-w-3xl mb-12">
        <Eyebrow>Operational Intelligence</Eyebrow>
        <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[48px] font-bold tracking-[-0.026em] leading-[1.05] mt-3" style={{ color: T.textPrimary }}>
          Every deal has <GradientWord>vital signs.</GradientWord>
        </h2>
        <p className="text-[15px] leading-relaxed mt-5" style={{ color: T.textMuted }}>
          Built on the same intelligence layer that powers every Deal Room. Calm, calibrated, mission-critical.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <IntelWidget Icon={Activity}     label="Deal Health Score"       value="82"  unit="/100" pill="Good"          pillTone="emerald" sub="Up 6% this week"           subTone="emerald" sparkPoints={[55,62,65,70,72,78,82]} sparkStroke="#10B981" />
        <IntelWidget Icon={TrendingUp}   label="Exchange Probability"    value="78"  unit="%"    pill="High"          pillTone="violet"  sub="Down 5% from last week"    subTone="rose"    sparkPoints={[68,72,75,80,82,80,78]} sparkStroke="#7C3AED" />
        <IntelWidget Icon={ShieldAlert}  label="Risk Detection"          value="2"   unit=""     pill="High priority" pillTone="rose"    sub="1 new today"                subTone="rose"    sparkPoints={[4,3,3,2,2,2,2]}        sparkStroke="#EF4444" />
        <IntelWidget Icon={PoundSterling} label="Fee At Risk"            value="£12.4K" unit=""  pill="Recoverable"   pillTone="amber"   sub="Awaiting solicitor sign-off" subTone="default" sparkPoints={[18,16,14,15,13,13,12]} sparkStroke="#F59E0B" />
        <IntelWidget Icon={GitBranch}    label="Chain Health"            value="3/4" unit=""     pill="On track"      pillTone="emerald" sub="2 dependent transactions"   subTone="default" />
        <IntelWidget Icon={Users}        label="Participant Responsiveness" value="92" unit="%" pill="Responsive"    pillTone="emerald" sub="Avg reply time · 2h 14m"    subTone="emerald" sparkPoints={[80,82,85,86,88,90,92]} sparkStroke="#10B981" />
        <IntelWidget Icon={Clock}        label="Mortgage Expiry"         value="9"   unit=" days" pill="Watch"         pillTone="amber"   sub="NatWest · Re-issuance ready" subTone="default" />
        <IntelWidget Icon={Briefcase}    label="Legal Progress"          value="6/9" unit=" stages" pill="In progress" pillTone="violet" sub="Enquiries pack outstanding" subTone="default" />
        <IntelWidget Icon={Bell}         label="Awaiting Actions"        value="4"   unit=""     pill="Today"         pillTone="violet"  sub="2 chases due · 1 approval"  subTone="default" />
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════
   SECTION 5 — MULTI-PARTY COLLABORATION
   ─────────────────────────────────────────────────────────────────── */
const PARTY_ROLES = [
  { role: "Buyer",       Icon: Users,        action: "Tracks progress · uploads ID · signs contracts",  color: "#0EA5E9" },
  { role: "Seller",      Icon: Home,         action: "Completes TA forms · approves milestones",        color: "#10B981" },
  { role: "Estate Agent",Icon: Briefcase,    action: "Coordinates · chases · maintains visibility",      color: "#7C3AED" },
  { role: "Broker",      Icon: PoundSterling,action: "Confirms AIP · monitors mortgage offer windows",   color: "#F59E0B" },
  { role: "Solicitor",   Icon: ShieldCheck,  action: "Drafts contracts · raises enquiries · exchanges",  color: "#6D28D9" },
];

const CollaborationSection = () => (
  <section className="py-20 lg:py-28" data-testid="gp-collab"
           style={{ background: T.surface }}>
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
      <div className="max-w-3xl mb-12">
        <Eyebrow>Multi-Party Collaboration</Eyebrow>
        <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[48px] font-bold tracking-[-0.026em] leading-[1.05] mt-3" style={{ color: T.textPrimary }}>
          Five parties. <GradientWord>One room.</GradientWord>
        </h2>
        <p className="text-[15px] leading-relaxed mt-5" style={{ color: T.textMuted }}>
          Every party transacts inside the same operational thread — structured updates, pinned actions,
          document requests, milestone approvals, full progression visibility. No more side conversations.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] gap-12 items-start">
        {/* Left — parties */}
        <ul className="space-y-3">
          {PARTY_ROLES.map(({ role, Icon, action, color }) => (
            <li key={role} className="rounded-2xl p-5 flex items-start gap-4" style={{ background: "#FFFFFF", border: T.border, boxShadow: "0 6px 14px -8px rgba(15,23,42,0.06)" }}>
              <span className="w-11 h-11 rounded-xl inline-flex items-center justify-center shrink-0 text-white" style={{ background: `linear-gradient(135deg, ${color}E6, ${color})`, boxShadow: `0 8px 18px -8px ${color}80, inset 0 1px 0 rgba(255,255,255,0.22)` }}>
                <Icon size={17} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-[15px] font-bold" style={{ color: T.textPrimary }}>{role}</p>
                <p className="text-[12.5px] mt-1 leading-snug" style={{ color: T.textSubtle }}>{action}</p>
              </div>
              <CheckCircle2 size={16} className="shrink-0 mt-1" style={{ color: "#10B981" }} />
            </li>
          ))}
        </ul>

        {/* Right — feature stack */}
        <div className="rounded-2xl p-6 lg:p-7 sticky top-24" style={{ background: "#FFFFFF", border: T.border, boxShadow: T.cardShadow }}>
          <Eyebrow>Inside the Deal Room</Eyebrow>
          <h3 className="font-display text-[20px] font-bold mt-2.5" style={{ color: T.textPrimary }}>What you see together</h3>
          <ul className="mt-5 space-y-3.5">
            {[
              { Icon: MessageSquare, t: "Structured updates",  sub: "Each party posts in context — no off-thread DMs." },
              { Icon: Target,        t: "Pinned actions",       sub: "Open requests stay visible until completed." },
              { Icon: FileText,      t: "Document requests",    sub: "Sent, tracked, signed — categorised by stage." },
              { Icon: Activity,      t: "Milestone tracking",   sub: "Every party sees the same progression rail." },
              { Icon: ShieldCheck,   t: "Structured approvals", sub: "Audit-ready sign-offs from the right party." },
              { Icon: Eye,           t: "Progression visibility", sub: "From instruction to completion — always live." },
            ].map(({ Icon, t, sub }) => (
              <li key={t} className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0" style={{ background: "#F5F3FF", color: T.violetDeep, border: "1px solid #E9D5FF" }}>
                  <Icon size={13} />
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] font-bold" style={{ color: T.textPrimary }}>{t}</p>
                  <p className="text-[11.5px] mt-0.5 leading-snug" style={{ color: T.textSubtle }}>{sub}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════
   SECTION 6 — DIRECTOR / MANAGER VISIBILITY
   ─────────────────────────────────────────────────────────────────── */
const DirectorSection = () => {
  const offices = [
    { name: "Manchester · Central",   exchanges: 18, pipeline: "£12.4M", health: 86, trend: "up" },
    { name: "Manchester · Didsbury", exchanges: 14, pipeline: "£9.2M",  health: 78, trend: "up" },
    { name: "Liverpool",             exchanges: 11, pipeline: "£7.8M",  health: 64, trend: "down" },
    { name: "Leeds · Headingley",    exchanges: 9,  pipeline: "£6.1M",  health: 81, trend: "up" },
  ];
  return (
    <section className="py-20 lg:py-28" data-testid="gp-director">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <Eyebrow>Director & Manager Oversight</Eyebrow>
          <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[48px] font-bold tracking-[-0.026em] leading-[1.05] mt-3" style={{ color: T.textPrimary }}>
            See the whole agency. <GradientWord>Make the right move.</GradientWord>
          </h2>
          <p className="text-[15px] leading-relaxed mt-5" style={{ color: T.textMuted }}>
            Office-level operational oversight. Pipeline forecasting, fee at risk, fall-through exposure,
            stuck transactions, team responsiveness — all in one enterprise-grade lens.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-6">
          {/* Office comparison table */}
          <div className="rounded-2xl overflow-hidden" style={{ background: "#FFFFFF", border: T.border, boxShadow: T.cardShadow }}>
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(15,23,42,0.05)" }}>
              <p className="font-display text-[15px] font-bold" style={{ color: T.textPrimary }}>Office performance · this quarter</p>
              <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] px-2 h-[20px] inline-flex items-center rounded-full" style={{ background: "#F5F3FF", color: T.violetDeep, border: "1px solid #E9D5FF" }}>Live</span>
            </div>
            <table className="w-full">
              <thead style={{ background: "#FAFBFD" }}>
                <tr className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: T.textFaint }}>
                  <th className="text-left px-5 py-2.5">Office</th>
                  <th className="text-right px-3 py-2.5">Exchanges</th>
                  <th className="text-right px-3 py-2.5">Pipeline</th>
                  <th className="text-right px-5 py-2.5">Health</th>
                </tr>
              </thead>
              <tbody>
                {offices.map((o) => (
                  <tr key={o.name} className="border-t" style={{ borderColor: "rgba(15,23,42,0.05)" }}>
                    <td className="px-5 py-3.5 text-[12.5px] font-semibold" style={{ color: T.textPrimary }}>{o.name}</td>
                    <td className="px-3 py-3.5 text-right text-[12.5px] tabular-nums" style={{ color: T.textMuted }}>{o.exchanges}</td>
                    <td className="px-3 py-3.5 text-right text-[12.5px] tabular-nums font-bold" style={{ color: T.violetDeep }}>{o.pipeline}</td>
                    <td className="px-5 py-3.5 text-right">
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-bold tabular-nums" style={{ color: o.health >= 80 ? "#047857" : o.health >= 70 ? "#B45309" : "#B91C1C" }}>
                        {o.health}{o.trend === "up" ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Right — director signals */}
          <div className="space-y-3">
            {[
              { Icon: TrendingUp,    t: "Exchange pipeline",    v: "52 deals", sub: "Forecast: £34.6M over next 8 weeks", tone: "violet" },
              { Icon: PoundSterling, t: "Fee forecasting",       v: "£458K",   sub: "Expected revenue · 92% confidence",   tone: "emerald" },
              { Icon: ShieldAlert,   t: "Fall-through exposure", v: "£87K",    sub: "3 deals at high risk · review now",   tone: "rose" },
              { Icon: Clock,         t: "Stuck transactions",    v: "9",       sub: "Median stall · 6.4 days",             tone: "amber" },
              { Icon: Users,         t: "Team responsiveness",   v: "94%",     sub: "Top quartile · last 30 days",         tone: "emerald" },
            ].map(({ Icon, t, v, sub, tone }) => {
              const ts = {
                violet:  { bg: "#F5F3FF", fg: T.violetDeep, border: "#E9D5FF" },
                emerald: { bg: "#ECFDF5", fg: "#047857",    border: "#A7F3D0" },
                rose:    { bg: "#FEF2F2", fg: "#B91C1C",    border: "#FECACA" },
                amber:   { bg: "#FFFBEB", fg: "#B45309",    border: "#FDE68A" },
              }[tone];
              return (
                <div key={t} className="rounded-2xl p-4 flex items-center gap-4" style={{ background: "#FFFFFF", border: T.border, boxShadow: "0 4px 12px -6px rgba(15,23,42,0.08)" }}>
                  <span className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0" style={{ background: ts.bg, color: ts.fg, border: `1px solid ${ts.border}` }}>
                    <Icon size={15} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11.5px] font-bold" style={{ color: T.textMuted }}>{t}</p>
                    <p className="font-display text-[18px] font-bold tabular-nums leading-none mt-0.5" style={{ color: T.textPrimary }}>{v}</p>
                    <p className="text-[10.5px] mt-1" style={{ color: T.textSubtle }}>{sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   SECTION 7 — MOBILE EXPERIENCE
   ─────────────────────────────────────────────────────────────────── */
const MobileSection = () => (
  <section className="py-20 lg:py-28 overflow-hidden relative" data-testid="gp-mobile"
           style={{ background: T.surface }}>
    {/* Ambient glow */}
    <div aria-hidden="true" className="absolute inset-0 -z-10 pointer-events-none"
         style={{ background: "radial-gradient(ellipse 800px 500px at 70% 50%, rgba(124,58,237,0.16) 0%, rgba(124,58,237,0) 60%)" }} />

    <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <Eyebrow>Built for the move</Eyebrow>
          <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[48px] font-bold tracking-[-0.026em] leading-[1.05] mt-3" style={{ color: T.textPrimary }}>
            Agents work <GradientWord>everywhere.</GradientWord> So does ChainFlow.
          </h2>
          <p className="text-[15px] leading-relaxed mt-5 max-w-[520px]" style={{ color: T.textMuted }}>
            Progression updates, push alerts, deal-room messaging, in-app approvals and live chain visibility —
            every operational lever, in your pocket.
          </p>

          <ul className="mt-7 space-y-4 max-w-[480px]">
            {[
              { Icon: Bell,         t: "Push alerts that matter",  sub: "Only the events that need an agent's response." },
              { Icon: MessageSquare,t: "Deal Room messaging",      sub: "Reply to solicitors and chase chain links from anywhere." },
              { Icon: CheckCircle2, t: "One-tap approvals",         sub: "Sign off documents and milestones in seconds." },
              { Icon: GitBranch,    t: "Live chain visibility",    sub: "Up and down the chain — instant status." },
            ].map(({ Icon, t, sub }) => (
              <li key={t} className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
                      style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #FFFFFF 100%)", border: "1px solid rgba(124,58,237,0.18)", color: T.violetDeep, boxShadow: "0 6px 14px -8px rgba(124,58,237,0.18), inset 0 1px 0 rgba(255,255,255,0.8)" }}>
                  <Icon size={15} strokeWidth={2.1} />
                </span>
                <div className="min-w-0">
                  <p className="font-display text-[14px] font-bold" style={{ color: T.textPrimary }}>{t}</p>
                  <p className="text-[12.5px] mt-0.5 leading-snug" style={{ color: T.textSubtle }}>{sub}</p>
                </div>
              </li>
            ))}
          </ul>

          <p className="mt-8 inline-flex items-center gap-3 text-[12px]" style={{ color: T.textSubtle }}>
            {[Apple, Smartphone, Tablet, Monitor].map((Icon, i) => (
              <Icon key={i} size={16} strokeWidth={1.8} style={{ color: T.textMuted }} />
            ))}
            <span>Available on iPhone, Android, iPad and desktop.</span>
          </p>
        </div>

        {/* iPhone composition */}
        <div className="relative flex items-center justify-center h-[600px]">
          <div aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 bottom-12 w-[60%] h-[60px] rounded-full opacity-60"
               style={{ background: "radial-gradient(ellipse at center, rgba(124,58,237,0.40) 0%, rgba(124,58,237,0) 65%)", filter: "blur(24px)" }} />

          <div className="relative w-[240px] h-[500px] rounded-[48px] p-[9px]"
               style={{ background: "linear-gradient(180deg, #1B1D3A 0%, #0B0C1E 100%)",
                        boxShadow: "0 50px 110px -30px rgba(60,40,180,0.55), 0 24px 60px -18px rgba(15,23,42,0.50), inset 0 0 0 1.5px rgba(255,255,255,0.06)" }}>
            <span className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[88px] h-[26px] rounded-full bg-black z-10" />
            <div className="w-full h-full rounded-[40px] overflow-hidden bg-white flex flex-col">
              <div className="px-5 pt-8 pb-3 flex items-center justify-between">
                <p className="font-display text-[14px] font-bold" style={{ color: T.textPrimary }}>Deal Room</p>
                <span className="text-[10px] font-bold uppercase tracking-[0.12em] px-1.5 h-[18px] inline-flex items-center rounded-full"
                      style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}>Live</span>
              </div>
              <div className="px-3 flex-1 overflow-hidden">
                {/* Hero exchange card */}
                <div className="rounded-xl p-3.5" style={{ background: T.gradientCTA, boxShadow: "0 12px 24px -10px rgba(99,102,241,0.55)" }}>
                  <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/80">Exchange in</p>
                  <p className="font-display text-[28px] font-bold tabular-nums leading-none text-white mt-1">9 days</p>
                  <p className="text-[10.5px] mt-1.5 text-white/85">23 King Street, Manchester</p>
                </div>
                {/* Push notifications */}
                <ul className="mt-3 space-y-1.5">
                  {[
                    { Icon: PoundSterling, t: "Mortgage offer issued", sub: "NatWest · 2 mins ago",  tone: "violet" },
                    { Icon: ShieldAlert,   t: "Solicitor inactive",    sub: "4 days · chase now",    tone: "amber" },
                    { Icon: CheckCircle2,  t: "Searches returned",     sub: "Local Authority",       tone: "emerald" },
                    { Icon: MessageSquare, t: "New message · Sarah Lee", sub: "Draft contract received", tone: "violet" },
                  ].map((row, i) => {
                    const tn = {
                      violet:  { bg: "#F5F3FF", fg: T.violetDeep },
                      amber:   { bg: "#FFFBEB", fg: "#B45309" },
                      emerald: { bg: "#ECFDF5", fg: "#047857" },
                    }[row.tone];
                    return (
                      <li key={i} className="flex items-center gap-2 rounded-lg p-2" style={{ background: "#FAFBFD", border: T.border }}>
                        <span className="w-6 h-6 rounded-md inline-flex items-center justify-center shrink-0" style={{ background: tn.bg, color: tn.fg }}>
                          <row.Icon size={11} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-bold truncate" style={{ color: T.textPrimary }}>{row.t}</p>
                          <p className="text-[9px] truncate" style={{ color: T.textSubtle }}>{row.sub}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              {/* Bottom approve action */}
              <div className="px-3 pb-3 pt-2">
                <button className="w-full h-9 rounded-lg text-[12px] font-bold inline-flex items-center justify-center gap-1.5 text-white" style={{ background: T.gradientCTA, boxShadow: T.ctaShadow }}>
                  Approve milestone <ArrowRight size={12} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════
   SECTION 8 — FINAL CTA
   ─────────────────────────────────────────────────────────────────── */
const FinalCTASection = () => (
  <section className="py-20 lg:py-28" data-testid="gp-final-cta">
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
      <div className="relative rounded-3xl p-10 lg:p-16 overflow-hidden text-center"
           style={{
             background: "linear-gradient(160deg, #1B1D3A 0%, #0E0F22 60%, #0B0C1E 100%)",
             boxShadow: "0 60px 120px -30px rgba(60,40,180,0.50)",
           }}>
        {/* Background motion */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
             style={{ background: "radial-gradient(ellipse 1000px 500px at 30% 30%, rgba(124,58,237,0.30) 0%, rgba(124,58,237,0) 60%), radial-gradient(ellipse 800px 400px at 80% 80%, rgba(99,102,241,0.22) 0%, rgba(99,102,241,0) 60%)" }} />

        <div className="relative max-w-3xl mx-auto">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] inline-flex items-center gap-1.5" style={{ color: "#A78BFA" }}>
            <span className="w-1.5 h-1.5 rounded-full cf-pulse" style={{ background: T.violet }} />
            Replace chaos with clarity
          </p>
          <h2 className="font-display text-[34px] sm:text-[44px] lg:text-[56px] font-bold tracking-[-0.028em] leading-[1.04] text-white mt-4">
            Replace transaction chaos with <span style={{ background: "linear-gradient(135deg, #C4B5FD 0%, #A78BFA 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>operational clarity.</span>
          </h2>
          <p className="text-[15px] lg:text-[17px] leading-relaxed mt-6 max-w-2xl mx-auto" style={{ color: "rgba(255,255,255,0.78)" }}>
            ChainFlow gives agencies one structured system for progression, communication,
            documents, and deal visibility.
          </p>

          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <PrimaryButton to="/signup" testid="gp-final-book-demo">Book Demo</PrimaryButton>
            <a href="/#landing-audit"
               className="h-12 px-6 rounded-xl text-[13.5px] font-semibold inline-flex items-center gap-2 transition-all hover:bg-white/10 text-white"
               style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)" }}
               data-testid="gp-final-audit">
              Run Operational Audit <ArrowRight size={14} />
            </a>
          </div>

          <p className="text-[12px] mt-7 inline-flex items-center gap-3" style={{ color: "rgba(255,255,255,0.55)" }}>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full cf-pulse" style={{ background: "#10B981" }} />
              Live with UK estate agencies
            </span>
            <span>·</span>
            <span>30-second onboarding</span>
            <span>·</span>
            <span>No card required</span>
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════
   Page composition
   ─────────────────────────────────────────────────────────────────── */
export default function GoldenPathPage() {
  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: "#FAFAFD" }} data-testid="golden-path-page">
      <TopNav />
      <HeroSection />
      <ProblemSection />
      <GoldenPathSection />
      <IntelligenceSection />
      <CollaborationSection />
      <DirectorSection />
      <MobileSection />
      <FinalCTASection />

      {/* Compact footer */}
      <footer className="py-10 px-6 lg:px-10 max-w-[1320px] mx-auto flex flex-wrap items-center justify-between gap-3 text-[11.5px]" style={{ color: T.textSubtle, borderTop: "1px solid rgba(15,23,42,0.06)" }}>
        <span>© 2026 ChainFlow OS Ltd. · The operating system for property transactions.</span>
        <div className="flex items-center gap-5">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <Link to="/pricing" className="hover:text-slate-900">Pricing</Link>
          <Link to="/login" className="hover:text-slate-900">Sign in</Link>
        </div>
      </footer>
    </div>
  );
}
